import React, { useState } from "react";
import "./Avatar.css";
import { isValidHttpImage } from "../../utils/helpers";

export interface AvatarProps {
  initials: string;
  extraCount?: number;
  isAddButton?: boolean;
  bgColor?: string;
  onClick?: () => void;
  className?: string;
  imageSrc?: string;
  imageClassName?: string;
  innerClassName?: string;
  unstyled?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({
  initials,
  extraCount,
  isAddButton,
  bgColor,
  onClick,
  className = "",
  imageSrc,
  imageClassName = "avatar-image",
  innerClassName = "avatar-inner",
  unstyled = false,
}) => {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const isValidImage = isValidHttpImage(imageSrc);

  const isBase64Image = imageSrc && imageSrc.startsWith("data:image");

  // F-636: a photo that fails to load (e.g. a 404 from blaze) falls back to the initials
  // through React state. The old onError rewrote the parent's textContent, removing the <img>
  // behind React's back; React's next commit then threw "removeChild ... not a child of this
  // node" and the error boundary took the whole page down. Never mutate the DOM here.
  // Keyed on the src, so a different photo is tried again.
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const imageFailed = !!imageSrc && failedSrc === imageSrc;

  const showImage = (isValidImage || isBase64Image) && !imageFailed;

  if (isAddButton) {
    return (
      <div
        className={unstyled ? className : `app-avatar app-avatar-add ${className}`}
        onClick={handleClick}
        style={bgColor ? { backgroundColor: bgColor } : undefined}
      >
        <div className={unstyled ? innerClassName : `avatar-inner ${innerClassName}`}>
          {showImage ? (
            <img
              src={imageSrc}
              alt={initials}
              className={` ${imageClassName}`}
              onError={() => setFailedSrc(imageSrc)}
            />
          ) : imageFailed ? (
            initials
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>
    );
  }

  if (extraCount !== undefined) {
    return (
      <div
        className={unstyled ? className : `app-avatar ${className}`}
        onClick={handleClick}
        style={bgColor ? { backgroundColor: bgColor } : undefined}
      >
        <div className={unstyled ? (innerClassName || "") : "avatar-inner"}>+{extraCount}</div>
      </div>
    );
  }

  return (
    <div
      className={unstyled ? className : `app-avatar ${className}`}
      onClick={handleClick}
      style={bgColor ? { backgroundColor: bgColor } : undefined}
    >
      <div className={unstyled ? innerClassName : `avatar-inner ${innerClassName}`}>
        {showImage ? (
          <img
            src={imageSrc}
            alt={initials}
            className={` ${imageClassName}`}
            onError={() => setFailedSrc(imageSrc)}
          />
        ) : (
          initials
        )}
      </div>
    </div>
  );
};

export default Avatar;
